import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ExternalLink, Edit, Trash2, Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

interface Grant {
  id: string;
  funder_program: string;
  program_focus: string | null;
  typical_grant_size: string | null;
  deadline_date: string | null;
  cycle_timeline: string | null;
  geography: string | null;
  eligibility_type: string | null;
  contact_name: string | null;
  contact_email: string | null;
  application_info_url: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

export const GrantsManagementTable = () => {
  const [grants, setGrants] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGrants = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('grants')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setGrants(data || []);
    } catch (err: any) {
      console.error('Error fetching grants:', err);
      setError(err.message);
      toast({
        title: 'Error',
        description: 'Failed to fetch grants',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrants();
  }, []);

  const getOpenCloseStatus = (grant: Grant) => {
    if (!grant.is_active) {
      return { status: 'Closed', variant: 'destructive' as const };
    }
    
    if (grant.deadline_date) {
      const deadline = new Date(grant.deadline_date);
      const now = new Date();
      if (deadline < now) {
        return { status: 'Closed', variant: 'destructive' as const };
      }
    }
    
    return { status: 'Open', variant: 'default' as const };
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const toggleGrantStatus = async (grantId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('grants')
        .update({ is_active: !currentStatus })
        .eq('id', grantId);

      if (error) throw error;

      await fetchGrants();
      toast({
        title: 'Success',
        description: `Grant ${!currentStatus ? 'activated' : 'deactivated'} successfully`
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: 'Failed to update grant status',
        variant: 'destructive'
      });
    }
  };

  const deleteGrant = async (grantId: string) => {
    if (!confirm('Are you sure you want to delete this grant?')) return;

    try {
      const { error } = await supabase
        .from('grants')
        .delete()
        .eq('id', grantId);

      if (error) throw error;

      await fetchGrants();
      toast({
        title: 'Success',
        description: 'Grant deleted successfully'
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: 'Failed to delete grant',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="text-center text-muted-foreground">Loading grants...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="text-center text-destructive">Error: {error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Grants Management</span>
          <Badge variant="secondary">{grants.length} total grants</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {grants.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            No grants found. Upload grants using the importer above.
          </div>
        ) : (
          <ScrollArea className="h-[600px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Funder/Program</TableHead>
                  <TableHead>Focus</TableHead>
                  <TableHead>Grant Size</TableHead>
                  <TableHead>Geography</TableHead>
                  <TableHead>Deadline</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grants.map((grant) => {
                  const statusInfo = getOpenCloseStatus(grant);
                  return (
                    <TableRow key={grant.id}>
                      <TableCell>
                        <Badge variant={statusInfo.variant}>
                          {statusInfo.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium max-w-[200px]">
                        <div className="truncate" title={grant.funder_program}>
                          {grant.funder_program}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[150px]">
                        <div className="truncate" title={grant.program_focus || ''}>
                          {grant.program_focus || '-'}
                        </div>
                      </TableCell>
                      <TableCell>{grant.typical_grant_size || '-'}</TableCell>
                      <TableCell className="max-w-[120px]">
                        <div className="truncate" title={grant.geography || ''}>
                          {grant.geography || '-'}
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(grant.deadline_date)}</TableCell>
                      <TableCell>
                        {grant.contact_email ? (
                          <a
                            href={`mailto:${grant.contact_email}`}
                            className="text-blue-600 hover:underline text-sm"
                            title={grant.contact_name || grant.contact_email}
                          >
                            {grant.contact_name || 'Contact'}
                          </a>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {grant.application_info_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => window.open(grant.application_info_url!, '_blank')}
                              title="View application info"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => toggleGrantStatus(grant.id, grant.is_active)}
                            title={`${grant.is_active ? 'Deactivate' : 'Activate'} grant`}
                          >
                            <Eye className={`h-3 w-3 ${grant.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                            onClick={() => deleteGrant(grant.id)}
                            title="Delete grant"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};